import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { extractGraphMetricsAggregates, normalizeHourGraphPayload } from "../../api/util";
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from "../../utils/dateTime";

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_HOUR = 60 * 60 * 1000;

// Stable empty-array reference shared across all instances. Used as the
// default for the `data`, `series` and `midlines` props so the destructure
// defaults don't allocate a fresh `[]` on every render — which would
// otherwise invalidate the `seriesList` memo (and the d3 draw effect's
// dependency), causing the chart to be entirely re-drawn (and keyboard
// focus state to be re-applied via `handleFocus`) on every parent
// re-render, e.g. when a sibling chart updates the shared page-level
// tooltip via `setTooltip`. That re-apply was the source of the
// "battle" where the focused chart kept reclaiming the tooltip from a
// sibling chart the cursor was actually hovering over.
const STABLE_EMPTY_ARRAY = Object.freeze([]);

// Fallback colors for series that don't specify their own `color`.
const DEFAULT_PALETTE = Object.freeze([
  "#D07F15",
  "#008900",
  "#1f6feb",
  "#b5179e",
  "#0aa3a3",
  "#e63946",
]);

/* =========================
   ADAPTIVE X-AXIS (custom-range mode)
   - Tick interval + label format are chosen from the range length so labels
     never crowd at any zoom level supported by the datepicker (2 days–2 yrs).
   ========================= */

function formatAxisValue(value) {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

const Y_AXIS_LABEL_FONT_SIZE = "10px";
const Y_AXIS_LABEL_FONT_FAMILY = "Arial, Helvetica, sans-serif";

function TooltipSeriesMarker({ color, dash }) {
  if (!dash) {
    return <span style={{ color }}>●</span>;
  }

  return (
    <svg
      width="18"
      height="10"
      aria-hidden="true"
      focusable="false"
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      <line
        x1="1"
        y1="5"
        x2="17"
        y2="5"
        stroke={color}
        strokeWidth="2"
        strokeDasharray={dash}
      />
    </svg>
  );
}

function applyYAxisLabelStyle(selection) {
  return selection
    .style("font-size", Y_AXIS_LABEL_FONT_SIZE)
    .style("font-family", Y_AXIS_LABEL_FONT_FAMILY)
    .style("font-weight", "bold")
    .attr("fill", "#000");
}

/**
 * Custom-range Y axis scaled to the data extent (min→max with padding).
 * Used when series share an axis but occupy a narrow band (e.g. SEL 80–100).
 */
function buildDataExtentYAxis(values) {
  if (!values.length) return null;
  let yMin = d3.min(values);
  let yMax = d3.max(values);
  if (yMin === yMax) {
    yMin -= 0.5;
    yMax += 0.5;
  } else {
    const pad = (yMax - yMin) * 0.05;
    yMin -= pad;
    yMax += pad;
  }
  const scale = d3.scaleLinear().domain([yMin, yMax]).nice();
  const [niceMin, niceMax] = scale.domain();
  return {
    domain: [niceMin, niceMax],
    tickValues: scale.ticks(5),
    midlines: [],
  };
}

/**
 * Custom-range Y axis for symmetric diverging charts (e.g. storage). Tick
 * labels sit at the domain ends and zero. Midline labels sit at ±50% of the
 * half-range for two-midline charts, or at +50% for a single midline.
 */
function buildCustomRangeYAxis(values, { diverging, midlineCount, unit }) {
  const label = v => `${formatAxisValue(v)} ${unit}`;

  if (diverging) {
    const extent = Math.max(
      Math.abs(d3.min(values) ?? 0),
      Math.abs(d3.max(values) ?? 0),
      1
    );
    const absMax = d3.scaleLinear().domain([0, extent * 1.1]).nice().domain()[1];
    const half = absMax / 2;
    const tickValues = [absMax, 0, -absMax];
    const midlines =
      midlineCount >= 2
        ? [
            { value: half, label: label(half) },
            { value: -half, label: label(-half) },
          ]
        : [{ value: half, label: label(half) }];
    return { domain: [-absMax, absMax], tickValues, midlines };
  }

  const max = Math.max(d3.max(values) ?? 0, 0);
  const yMax = d3.scaleLinear().domain([0, max * 1.1 || 1]).nice().domain()[1];
  const half = yMax / 2;
  return {
    domain: [0, yMax],
    tickValues: [yMax, 0],
    midlines: [{ value: half, label: label(half) }],
  };
}

// Coerce a raw payload (array of rows with date/value-ish fields) into the
// sorted `{ date, value }[]` the chart draws from.
function applyMappedValue(value, mapValue) {
  if (!Number.isFinite(value) || typeof mapValue !== 'function') {
    return value;
  }
  const mapped = Number(mapValue(value));
  return Number.isFinite(mapped) ? mapped : value;
}

function normalizeRows(rows, mapValue) {
  return (Array.isArray(rows) ? rows : [])
    .map(row => {
      const t = row?.date ?? row?.time ?? row?.timestamp ?? row?.t;
      const date = t instanceof Date ? t : t != null ? new Date(t) : null;
      const raw =
        row?.value ?? row?.right ?? row?.power_mw ?? row?.y ?? row?.mwh;
      const value = applyMappedValue(raw != null ? Number(raw) : NaN, mapValue);
      const point = { date, value };
      const valueMin = row?.valueMin != null ? Number(row.valueMin) : NaN;
      const valueMax = row?.valueMax != null ? Number(row.valueMax) : NaN;
      if (Number.isFinite(valueMin)) point.valueMin = applyMappedValue(valueMin, mapValue);
      if (Number.isFinite(valueMax)) point.valueMax = applyMappedValue(valueMax, mapValue);
      return point;
    })
    .filter(
      d =>
        d.date != null &&
        !Number.isNaN(d.date.getTime()) &&
        !Number.isNaN(d.value)
    )
    .sort((a, b) => a.date - b.date);
}

/**
 * Multi-line time chart with snap-to-nearest crosshair tooltip behavior, in
 * the same style as DualAxisLineChart. Renders one line per series and
 * displays as many lines as the data source provides.
 *
 * Series can be supplied two ways:
 *  - Multi-series: pass `series` as an array of `{ data, color, label }`. The
 *    chart draws one line per entry and the tooltip lists every series' value
 *    at the snapped point.
 *  - Single-series (backward compatible): pass `data` (+ `color`) directly, or
 *    a `dataEndpoint` to fetch from. Behaves exactly like the former
 *    single-line chart, including the grow-on-hover circle highlight.
 *
 * Supports two x-axis modes that share the same drawing/interaction code:
 *  - Fixed-duration (`duration='24hr'|'48hr'`): hourly ticks with labels and
 *    longer marks every 6 hours, per-point circles, and a hover highlight.
 *  - Custom range (no `duration`; pass `startDate`/`endDate`): adaptive tick
 *    density via `pickXAxisConfig`, per-point circles only for ranges up to
 *    ~1 month, and a dedicated hover dot so the snapped point reads clearly
 *    even when individual circles are hidden.
 *
 * All visual differences (color, Y domain, ticks, midlines, wrapper class,
 * header) are passed in as props so the same component can render the
 * grid / solar / storage variants from the dashboard configuration.
 *
 * @param {object} props
 * @param {'24hr'|'48hr'} [props.duration]  Fixed-duration mode: X-axis tick density and date-label rule. Omit for custom-range mode.
 * @param {Date} [props.startDate]          Custom-range start (inclusive). Accepted for API symmetry; axis scaling uses the data extent.
 * @param {Date} [props.endDate]            Custom-range end (inclusive). Accepted for API symmetry; axis scaling uses the data extent.
 * @param {string} [props.color]            Line / circle / horizontal-crosshair color for the single-series path.
 * @param {[number, number]} props.domain Y-axis domain `[min, max]`.
 * @param {number[]} props.tickValues     Y-axis tick values.
 * @param {string} props.unit             Y-axis tick unit (e.g. `'MW'`).
 * @param {string} props.tooltipUnit      Unit shown beside the tooltip value (e.g. `'MWh'`).
 * @param {Array<{ value: number, label: string }>} props.midlines  Dashed horizontal reference lines.
 * @param {string} props.wrapperClassName Outer `<div>` className (theming hook).
 * @param {React.ReactNode} props.headerLeft   Left-aligned header content.
 * @param {React.ReactNode} props.headerRight  Right-aligned header content.
 * @param {string} [props.dataEndpoint]   Backend URL; ignored when `data` or `series` is provided.
 * @param {Array<{ date: Date|string, value: number }>} [props.data]  Inline single-series data (controlled).
 * @param {Array<{ data: Array, color?: string, label?: string, dash?: string }>} [props.series]  Inline multi-series data. Takes precedence over `data`/`dataEndpoint`. `dash` is a CSS `stroke-dasharray` (omit for a solid line).
 * @param {function} [props.onAggregatesLoaded]  `(aggregates) => void` called after a successful endpoint fetch with `{ total }`.
 * @param {function} [props.onPlottedTotal]  `({ total, max } | null) => void` called with the sum and maximum of plotted Y values, or null when the chart has no points.
 * @param {function} [props.onTooltip]    `(state) => void` setter for the page-level tooltip.
 * @param {number} [props.height]         SVG height in px. Defaults to 140.
 * @param {boolean} [props.enableZoom]    Enable wheel / drag / keyboard (+, -, 0) zoom on the x axis.
 * @param {boolean} [props.scaleYToDataExtent]  Custom-range mode: scale Y from data min to max (with padding) instead of 0→max or symmetric diverging.
 * @param {boolean} [props.fixedYDomain]  Custom-range mode: keep the configured `domain`, `tickValues`, and `midlines` instead of auto-scaling Y to the data.
 * @param {function} [props.tickFormat]   Y-axis tick label formatter. Defaults to `` `${value} ${unit}` ``.
 * @param {boolean} [props.yGrid]        Draw a light solid horizontal line at each Y tick.
 * @param {boolean} [props.dailyTicks]    Custom-range: tick every day, label every week as mm/dd/yyyy.
 * @param {boolean} [props.showLegend]     Render a bottom-left key from series labels/colors/dash.
 * @param {boolean} [props.showHoverDots]  Show snap-point circles on hover. Defaults to true.
 */
const MultiLineChart = ({
  duration,
  startDate,
  endDate,
  color,
  domain,
  tickValues,
  unit,
  tooltipUnit,
  midlines = STABLE_EMPTY_ARRAY,
  wrapperClassName = "",
  headerLeft = null,
  headerRight = null,
  dataEndpoint = "",
  data = STABLE_EMPTY_ARRAY,
  series = STABLE_EMPTY_ARRAY,
  onTooltip,
  onAggregatesLoaded,
  onPlottedTotal,
  height = 140,
  monthlyTicks = false,
  renderTooltipExtra = null,
  showPoints = null,
  enableZoom = false,
  scaleYToDataExtent = false,
  fixedYDomain = false,
  timeZone = DEFAULT_TIME_ZONE,
  tickFormat = null,
  mapValue = null,
  yGrid = false,
  dailyTicks = false,
  showLegend = false,
  showHoverDots = true,
}) => {
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(timeZone),
    [timeZone],
  );
  const svgRef = useRef(null);
  const wrapperRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;
  const onAggregatesLoadedRef = useRef(onAggregatesLoaded);
  onAggregatesLoadedRef.current = onAggregatesLoaded;
  const onPlottedTotalRef = useRef(onPlottedTotal);
  onPlottedTotalRef.current = onPlottedTotal;
  const reactId = useId();
  const safeId = reactId.replace(/:/g, "");
  const clipPlotId = useId().replace(/:/g, "");

  // No `duration` => adaptive custom-range mode (variable tick density).
  const isCustomRange = !duration;

  // Keyboard navigation: which point is selected and whether arrow-key driven
  // navigation is currently active. Refs (not state) so that imperative d3
  // handlers can read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  const [dimensions, setDimensions] = useState({ width: 800, height });
  const [fetchedData, setFetchedData] = useState([]);

  useEffect(() => {
    if (!dataEndpoint) {
      setFetchedData([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const raw = await apiFetch(dataEndpoint);
        const normalized = normalizeHourGraphPayload(raw);
        if (!cancelled) {
          setFetchedData(normalized);
          onAggregatesLoadedRef.current?.(extractGraphMetricsAggregates(raw));
        }
      } catch (apiError) {
        console.warn(`API fetch failed for ${dataEndpoint}:`, apiError);
        if (!cancelled) {
          setFetchedData([]);
          onAggregatesLoadedRef.current?.(null);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dataEndpoint]);

  // Normalize every series down to `{ color, label, data: {date,value}[] }`.
  // The `series` prop takes precedence; otherwise we build a single series
  // from the inline `data` / fetched endpoint payload.
  const seriesList = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) {
      return series.map((s, i) => ({
        color: s?.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
        label: s?.label ?? "",
        dash: s?.dash ?? null,
        data: normalizeRows(s?.data, mapValue),
      }));
    }
    const single = dataEndpoint ? fetchedData : data;
    return [
      {
        color: color ?? DEFAULT_PALETTE[0],
        label: "",
        dash: null,
        data: normalizeRows(single, mapValue),
      },
    ];
  }, [series, data, fetchedData, dataEndpoint, color, mapValue]);

  useEffect(() => {
    const notify = onPlottedTotalRef.current;
    if (!notify) return undefined;
    const values = seriesList.flatMap((series) => series.data.map((point) => point.value));
    if (values.length === 0) {
      notify(null);
      return undefined;
    }
    notify({
      total: values.reduce((sum, value) => sum + value, 0),
      max: Math.max(...values),
    });
    return undefined;
  }, [seriesList]);

  const isMulti = seriesList.length > 1;
  const hasData = seriesList.some(s => s.data.length > 0);

  // Responsive resizing using ResizeObserver (handles sidebar open/close)
  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const el = wrapperRef.current;

    const update = () => {
      const { width } = el.getBoundingClientRect();
      setDimensions({ width: Math.max(0, Math.floor(width)), height });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    el.addEventListener("transitionend", update);

    return () => {
      ro.disconnect();
      el.removeEventListener("transitionend", update);
    };
  }, [height]);

  // Draw chart whenever data or dimensions change
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // No data to draw (e.g. the selected range contains no points). Bail after
    // clearing so a stale chart doesn't linger when the range changes.
    if (!hasData) return;

    const { width, height: chartHeight } = dimensions;
    const yTickPadding = 10;
    const margin = { top: 20, right: 72, bottom: dailyTicks ? 48 : 40, left: 20 };

    const allValues = seriesList.flatMap(s => s.data.flatMap(p => {
      const values = [p.value];
      if (Number.isFinite(p.valueMin)) values.push(p.valueMin);
      if (Number.isFinite(p.valueMax)) values.push(p.valueMax);
      return values;
    }));
    let yMin;
    let yMax;
    let effectiveTickValues;
    let effectiveMidlines;

    // Grid uses an asymmetric domain (e.g. -100..700): export headroom is much
    // larger than import. Symmetric auto-scaling would mirror export as import
    // (e.g. -700..700) and misplace midlines. Keep the configured axis instead.
    const isAsymmetricDiverging =
      domain[0] < 0 && Math.abs(domain[0]) !== domain[1];

    if (isCustomRange && allValues.length > 0 && scaleYToDataExtent) {
      const yCfg = buildDataExtentYAxis(allValues);
      if (yCfg) {
        [yMin, yMax] = yCfg.domain;
        effectiveTickValues = yCfg.tickValues;
        effectiveMidlines = yCfg.midlines;
      } else {
        [yMin, yMax] = domain;
        effectiveTickValues = tickValues;
        effectiveMidlines = midlines;
      }
    } else if (
      isCustomRange &&
      allValues.length > 0 &&
      !isAsymmetricDiverging &&
      !fixedYDomain
    ) {
      const yCfg = buildCustomRangeYAxis(allValues, {
        diverging: domain[0] < 0,
        midlineCount: midlines.length,
        unit,
      });
      [yMin, yMax] = yCfg.domain;
      effectiveTickValues = yCfg.tickValues;
      effectiveMidlines = yCfg.midlines;
    } else {
      [yMin, yMax] = domain;
      effectiveTickValues = tickValues;
      effectiveMidlines = midlines;
    }

    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

    // Combined x extent across every series, plus the sorted union of all
    // point timestamps used as snap "anchors" for the crosshair / keyboard
    // navigation. Per-series time->value maps let us look a series' value up
    // at any anchor (a series may be missing points other series have).
    const anchorTimes = new Set();
    let minTime = Infinity;
    let maxTime = -Infinity;
    seriesList.forEach(s => {
      s.data.forEach(p => {
        const t = p.date.getTime();
        anchorTimes.add(t);
        if (t < minTime) minTime = t;
        if (t > maxTime) maxTime = t;
      });
    });
    const anchors = Array.from(anchorTimes)
      .sort((a, b) => a - b)
      .map(t => new Date(t));
    const byTimeMaps = seriesList.map(
      s => new Map(s.data.map(p => [p.date.getTime(), p.value]))
    );

    // Always scale the axis to the actual data extent so the plotted line
    // spans the full chart width (first point at the left edge, last at the
    // right) with no leading/trailing empty gap. Monthly-tick mode only
    // changes how ticks are placed (one labeled tick per month), not the
    // domain.
    const useMonthlyAxis = monthlyTicks && startDate && endDate;

    const xScale = d3
      .scaleTime()
      .domain([new Date(minTime), new Date(maxTime)])
      .range([margin.left, width - margin.right]);

    // Current x scale used for all drawing / interaction. When zooming is
    // enabled this is replaced by `transform.rescaleX(xScale)` on each zoom
    // event; otherwise it stays equal to the base `xScale`.
    let zx = xScale;

    // The d3-zoom behavior, assigned only when `enableZoom` is set. Referenced
    // by the keyboard handler so +/- keys can zoom the focused chart.
    let zoomBehavior = null;

    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .range([chartHeight - margin.bottom, margin.top]);

    const days = (maxTime - minTime) / MS_PER_DAY;

    // X axis. In fixed-duration mode there is an unlabeled tick at every hour
    // boundary, with labels and longer tick marks at each 6-hour mark (00:00,
    // 06:00, 12:00, 18:00); the 00:00 mark shows the date instead of the time
    // so day boundaries stay anchored regardless of where the rolling window
    // starts. In custom-range mode the tick interval and label format adapt to
    // the range length so labels never crowd.
    let xAxis;
    let isLabeledTick = () => false;
    if (useMonthlyAxis) {
      // One labeled tick per calendar month in the data extent.
      // `timeMonth.range` is half-open, so offset the upper bound by one month
      // to keep the final month's tick. Do not clamp ticks to the exact point
      // instants — month anchors are often mid-day (e.g. UTC noon) so a
      // `d >= minDate` filter would drop the first month's label.
      const minDate = new Date(minTime);
      const maxDate = new Date(maxTime);
      const monthTicks = d3.timeMonth.range(
        d3.timeMonth.floor(minDate),
        d3.timeMonth.offset(d3.timeMonth.floor(maxDate), 1),
      );
      xAxis = d3
        .axisBottom(xScale)
        .tickValues(monthTicks)
        .tickFormat(dateFormatter.formatAxisMonthShortYear);
    } else if (dailyTicks) {
      const xMinDay = d3.timeDay.floor(new Date(minTime));
      const xMaxDay = d3.timeDay.floor(new Date(maxTime));
      const dayTicks = d3.timeDay.range(
        xMinDay,
        d3.timeDay.offset(xMaxDay, 1),
      );
      const startMs = xMinDay.getTime();
      isLabeledTick = d =>
        Math.round((d.getTime() - startMs) / MS_PER_DAY) % 7 === 0;
      xAxis = d3
        .axisBottom(xScale)
        .tickValues(dayTicks)
        .tickFormat(d =>
          isLabeledTick(d) ? dateFormatter.formatAxisMdY(d) : "",
        );
    } else if (isCustomRange) {
      const { interval, fmt } = dateFormatter.pickXAxisConfig(days);
      xAxis = d3.axisBottom(xScale).ticks(interval).tickFormat(fmt);
    } else {
      xAxis = d3
        .axisBottom(xScale)
        .ticks(d3.timeHour.every(1))
        .tickFormat(d => {
          const h = dateFormatter.getHour(d);
          if (h % 6 !== 0) return "";
          return h === 0 ? dateFormatter.formatAxisDate(d) : dateFormatter.formatAxisTime(d);
        });
    }

    const yAxis = d3
      .axisRight(yScale)
      .tickValues(effectiveTickValues)
      .tickPadding(yTickPadding)
      .tickFormat(d =>
        typeof tickFormat === "function"
          ? tickFormat(d)
          : `${formatAxisValue(d)} ${unit}`
      );
    if (yGrid) {
      yAxis.tickSizeInner(0);
    }

    // X axis
    const xAxisGroup = svg
      .append("g")
      .attr("transform", `translate(0,${chartHeight - margin.bottom})`)
      .call(xAxis);

    // Re-applies the tick text / tick-mark styling. Pulled into a function so
    // it can run again after a zoom redraws the axis with a rescaled scale.
    const styleXAxis = () => {
      xAxisGroup
        .selectAll("text")
        .style("text-anchor", "start")
        .style("font-weight", "bold")
        .attr("dx", "0.5em");

      if (dailyTicks) {
        xAxisGroup.selectAll(".tick line").attr("y2", d => (isLabeledTick(d) ? 12 : 5));
      } else if (isCustomRange) {
        xAxisGroup.selectAll(".tick line").attr("y2", 10);
      } else {
        xAxisGroup
          .selectAll(".tick line")
          .attr("y2", d => (dateFormatter.getHour(d) % 6 === 0 ? 15 : 6));
      }
    };

    styleXAxis();

    // Y axis (right)
    const yAxisGroup = svg
      .append("g")
      .attr("transform", `translate(${width - margin.right},0)`)
      .call(yAxis);

    applyYAxisLabelStyle(yAxisGroup.selectAll("text"));

    // Zero reference line (skipped when yGrid already draws a line at 0).
    if (!yGrid) {
      svg
        .append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", yScale(0))
        .attr("y2", yScale(0))
        .attr("stroke", "gray")
        .attr("stroke-dasharray", "4");
    }

    if (yGrid) {
      effectiveTickValues.forEach(value => {
        svg
          .append("line")
          .attr("x1", margin.left)
          .attr("x2", width - margin.right)
          .attr("y1", yScale(value))
          .attr("y2", yScale(value))
          .attr("stroke", "#d9d9d9")
          .attr("stroke-width", 1);
      });
    }

    // Configurable midlines (labels only when not already shown on the axis).
    const tickLabelValues = new Set(effectiveTickValues);
    effectiveMidlines.forEach(mid => {
      svg
        .append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", yScale(mid.value))
        .attr("y2", yScale(mid.value))
        .attr("stroke", "#bbb")
        .attr("stroke-dasharray", mid.dash ?? "2,2");

      if (!tickLabelValues.has(mid.value)) {
        applyYAxisLabelStyle(
          svg
            .append("text")
            .attr("x", width - margin.right + yTickPadding)
            .attr("y", yScale(mid.value) + 4)
            .text(mid.label),
        );
      }
    });

    // Plot bounds
    const plotLeft = margin.left;
    const plotRight = width - margin.right;
    const plotTop = margin.top;
    const plotBottom = chartHeight - margin.bottom;
    const plotW = Math.max(0, plotRight - plotLeft);
    const plotH = Math.max(0, plotBottom - plotTop);

    // Clip the lines / circles to the plot area
    svg
      .append("defs")
      .append("clipPath")
      .attr("id", clipPlotId)
      .append("rect")
      .attr("x", plotLeft)
      .attr("y", plotTop)
      .attr("width", plotW)
      .attr("height", plotH);

    // Line generator bound to a supplied x scale so it can be regenerated
    // against the zoomed scale (`zx`) without rebuilding the whole chart.
    const makeLine = sx =>
      d3
        .line()
        .x(d => sx(d.date))
        .y(d => yScale(clamp(d.value, yMin, yMax)))
        .curve(d3.curveMonotoneX);

    const plotContent = svg
      .append("g")
      .attr("class", "plot-clipped")
      .attr("clip-path", `url(#${clipPlotId})`);

    // Fixed-duration single-series mode keeps the original grow-on-hover
    // circle highlight. Every other case (custom range, or any multi-series
    // chart) uses dedicated hover dots — one per series — so each line's
    // snapped point stays legible.
    const useGrowHighlight = !isCustomRange && !isMulti;
    const circleClass = `line-pt-${safeId}`;

    // Custom-range mode hides per-point circles for ranges longer than
    // ~1 month (otherwise the dots crowd the lines and read as noise). A
    // caller can override this either way via the `showPoints` prop (e.g. the
    // benchmark chart plots one point per month and wants them always shown).
    const maxPointsWithCircles = isCustomRange ? Infinity : 72;
    const showCircles = showPoints != null
      ? showPoints
      : isCustomRange
        ? days <= 31
        : seriesList.every(s => s.data.length <= maxPointsWithCircles);

    // One line (and optional circle set) per series. Keep references to each
    // path / circle selection so a zoom event can re-position them against the
    // rescaled x scale.
    const seriesPaths = [];
    const seriesCircles = [];
    seriesList.forEach((s, si) => {
      const path = plotContent
        .append("path")
        .datum(s.data)
        .attr("fill", "none")
        .attr("stroke", s.color)
        .attr("stroke-width", 2)
        .attr("vector-effect", "non-scaling-stroke")
        .attr("d", makeLine(zx));
      if (s.dash) {
        path.attr("stroke-dasharray", s.dash);
      }
      seriesPaths.push(path);

      if (showCircles) {
        const circles = plotContent
          .selectAll(`.${circleClass}-${si}`)
          .data(s.data)
          .enter()
          .append("circle")
          .attr("class", `${circleClass} ${circleClass}-${si}`)
          .attr("data-anchor", d => d.date.getTime())
          .attr("cx", d => zx(d.date))
          .attr("cy", d => yScale(clamp(d.value, yMin, yMax)))
          .attr("r", 3)
          .attr("fill", s.color)
          .style("pointer-events", "none");
        seriesCircles.push(circles);
      } else {
        seriesCircles.push(null);
      }
    });

    // Week ambient and wind series include a daily high and low on the same
    // timestamp as the average. Draw those as endpoints of a stem so they stay
    // readable next to the mean line. Other charts omit valueMin / valueMax.
    const rangePoints = !isMulti
      ? seriesList[0].data.filter(
          (p) => Number.isFinite(p.valueMin) && Number.isFinite(p.valueMax),
        )
      : [];
    if (rangePoints.length) {
      const rangeColor = seriesList[0].color;
      plotContent
        .selectAll(`.range-stem-${safeId}`)
        .data(rangePoints)
        .enter()
        .append('line')
        .attr('class', `range-stem-${safeId}`)
        .attr('x1', (d) => zx(d.date))
        .attr('x2', (d) => zx(d.date))
        .attr('y1', (d) => yScale(clamp(d.valueMin, yMin, yMax)))
        .attr('y2', (d) => yScale(clamp(d.valueMax, yMin, yMax)))
        .attr('stroke', rangeColor)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.65)
        .style('pointer-events', 'none');

      ['valueMin', 'valueMax'].forEach((key) => {
        plotContent
          .selectAll(`.range-pt-${safeId}-${key}`)
          .data(rangePoints)
          .enter()
          .append('circle')
          .attr('class', `range-pt-${safeId}-${key}`)
          .attr('cx', (d) => zx(d.date))
          .attr('cy', (d) => yScale(clamp(d[key], yMin, yMax)))
          .attr('r', 3)
          .attr('fill', rangeColor)
          .style('pointer-events', 'none');
      });
    }

    // Crosshair (vertical guide + horizontal value guide for single series)
    const crosshair = svg
      .append("g")
      .attr("class", "crosshair")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const crosshairV = crosshair
      .append("line")
      .attr("stroke", "rgba(0,0,0,0.35)")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3")
      .attr("vector-effect", "non-scaling-stroke");

    // Horizontal value guide only makes sense for a single series; with
    // several lines it would be ambiguous which value it tracks.
    const crosshairH = isMulti
      ? null
      : crosshair
          .append("line")
          .attr("stroke", seriesList[0].color)
          .attr("stroke-opacity", 0.45)
          .attr("stroke-width", 1)
          .attr("vector-effect", "non-scaling-stroke");

    // Dedicated hover dots (one per series) used whenever we're not growing
    // the underlying circle. Always emphasize the snapped point even when
    // per-point circles are hidden.
    const hoverDots =
      useGrowHighlight || !showHoverDots
        ? []
        : seriesList.map(s =>
            plotContent
              .append("circle")
              .attr("class", "hover-dot")
              .attr("r", 6)
              .attr("fill", s.color)
              .style("pointer-events", "none")
              .style("opacity", 0)
          );

    const nearestIndexByPx = px => {
      let best = 0;
      let bestDx = Infinity;
      for (let i = 0; i < anchors.length; i++) {
        const dx = Math.abs(zx(anchors[i]) - px);
        if (dx < bestDx) {
          bestDx = dx;
          best = i;
        }
      }
      return best;
    };

    // Snap to the anchor whose timestamp is closest to `targetTime`. Used by
    // Shift+arrow keyboard navigation to jump forward/back one hour.
    const nearestIndexByTime = targetTime => {
      let best = 0;
      let bestDt = Infinity;
      for (let i = 0; i < anchors.length; i++) {
        const dt = Math.abs(anchors[i].getTime() - targetTime);
        if (dt < bestDt) {
          bestDt = dt;
          best = i;
        }
      }
      return best;
    };

    const updateCrosshair = anchorIdx => {
      const anchor = anchors[anchorIdx];
      const t = anchor.getTime();
      const xSnap = zx(anchor);
      crosshairV
        .attr("x1", xSnap)
        .attr("x2", xSnap)
        .attr("y1", plotTop)
        .attr("y2", plotBottom);

      if (crosshairH) {
        const v = byTimeMaps[0].get(t);
        if (v != null) {
          const y = yScale(clamp(v, yMin, yMax));
          crosshairH
            .attr("x1", plotLeft)
            .attr("x2", plotRight)
            .attr("y1", y)
            .attr("y2", y)
            .style("opacity", 1);
        } else {
          crosshairH.style("opacity", 0);
        }
      }

      hoverDots.forEach((dot, si) => {
        const v = byTimeMaps[si].get(t);
        if (v == null) {
          dot.style("opacity", 0);
          return;
        }
        dot
          .attr("cx", xSnap)
          .attr("cy", yScale(clamp(v, yMin, yMax)))
          .style("opacity", 1);
      });
    };

    // Tooltip date format. Fixed-duration mode shows month/day/time. Custom
    // range widens to include the year, and drops the time component once the
    // range is long enough that an hour:minute reading is meaningless.
    // Monthly-tick charts are calendar months — show month/year only (no day).
    const tooltipDateFmt = useMonthlyAxis
      ? { year: "numeric", month: "long" }
      : isCustomRange
        ? days > 31
          ? { year: "numeric", month: "long", day: "2-digit" }
          : { year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }
        : { month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false };

    const tooltipContentFor = anchorIdx => {
      const anchor = anchors[anchorIdx];
      const t = anchor.getTime();
      const dateText = dateFormatter.formatDate(anchor, tooltipDateFmt);

      if (!isMulti) {
        const v = byTimeMaps[0].get(t);
        const range = seriesList[0].data.find((p) => p.date.getTime() === t);
        return (
          <>
            <strong>X:</strong> {dateText}
            <br />
            <strong>Y:</strong> {v} {tooltipUnit}
            {range?.valueMax != null ? (
              <>
                <br />
                <strong>High:</strong> {range.valueMax} {tooltipUnit}
              </>
            ) : null}
            {range?.valueMin != null ? (
              <>
                <br />
                <strong>Low:</strong> {range.valueMin} {tooltipUnit}
              </>
            ) : null}
          </>
        );
      }

      // Per-series values at this anchor, keyed by label, for any caller-
      // supplied derived rows (e.g. Difference / Variance).
      const valuesByLabel = {};
      seriesList.forEach((s, si) => {
        if (s.label) valuesByLabel[s.label] = byTimeMaps[si].get(t);
      });

      return (
        <>
          <strong>X:</strong> {dateText}
          {seriesList.map((s, si) => {
            const v = byTimeMaps[si].get(t);
            if (v == null) return null;
            return (
              <div key={si}>
                <TooltipSeriesMarker color={s.color} dash={s.dash} />{" "}
                {s.label ? <strong>{s.label}:</strong> : null} {v} {tooltipUnit}
              </div>
            );
          })}
          {typeof renderTooltipExtra === "function"
            ? renderTooltipExtra(valuesByLabel)
            : null}
        </>
      );
    };

    const setHighlightByIndex = anchorIdx => {
      if (!useGrowHighlight) return;
      const t = anchorIdx >= 0 ? anchors[anchorIdx].getTime() : null;
      svg.selectAll(`.${circleClass}`).each(function () {
        const at = +this.getAttribute("data-anchor");
        d3.select(this).attr("r", at === t ? 10 : 3);
      });
      if (t != null) {
        svg
          .selectAll(`.${circleClass}`)
          .filter(function () {
            return +this.getAttribute("data-anchor") === t;
          })
          .raise();
      }
    };

    // Position the page-level tooltip anchored to the snapped data point
    // (centered horizontally on the crosshair, just above the highest series
    // value at that anchor) rather than following the mouse pointer. This
    // keeps the tooltip stable as the cursor moves within a snap region and
    // is friendlier on touch devices. Internal scale coordinates already
    // include the margins and map 1:1 to CSS pixels (no viewBox), so we
    // offset directly from the SVG's bounding rect.
    const showTooltipForPoint = anchorIdx => {
      const tip = onTooltipRef.current;
      if (!tip) return;
      const svgEl = svgRef.current;
      if (!svgEl) return;
      const anchor = anchors[anchorIdx];
      const t = anchor.getTime();
      const rect = svgEl.getBoundingClientRect();
      const xSnap = zx(anchor);

      // Anchor vertically to the topmost (smallest y px) series value present.
      let topY = Infinity;
      byTimeMaps.forEach(m => {
        const v = m.get(t);
        if (v != null) {
          const y = yScale(clamp(v, yMin, yMax));
          if (y < topY) topY = y;
        }
      });
      if (!Number.isFinite(topY)) topY = plotTop;

      tip({
        visible: true,
        content: tooltipContentFor(anchorIdx),
        x: rect.left + xSnap,
        y: rect.top + topY - 12,
      });
    };

    // Show the crosshair + tooltip snapped to a specific anchor index. Used by
    // both the keyboard navigation handlers and the pointerleave -> keyboard
    // fall-back path.
    const showSelectionByIndex = idx => {
      if (!anchors.length) return;
      const clamped = Math.max(0, Math.min(anchors.length - 1, idx));
      keyboardIndexRef.current = clamped;
      crosshair.style("opacity", 1);
      updateCrosshair(clamped);
      setHighlightByIndex(clamped);
      showTooltipForPoint(clamped);
    };

    const hideSelection = () => {
      crosshair.style("opacity", 0);
      setHighlightByIndex(-1);
      hoverDots.forEach(dot => dot.style("opacity", 0));
      const tip = onTooltipRef.current;
      if (tip) {
        tip(t => ({ ...t, visible: false }));
      }
    };

    const overlay = svg
      .append("rect")
      .attr("class", "hover-overlay")
      .attr("x", plotLeft)
      .attr("y", plotTop)
      .attr("width", plotW)
      .attr("height", plotH)
      .attr("fill", "transparent")
      .style("cursor", "crosshair")
      .on("pointerenter", event => {
        // Any real pointer movement on the chart kicks us out of keyboard
        // navigation mode and back into pointer-follow mode.
        keyboardModeRef.current = false;
        const [px] = d3.pointer(event, svgRef.current);
        showSelectionByIndex(nearestIndexByPx(px));
      })
      .on("pointermove", event => {
        keyboardModeRef.current = false;
        const [px] = d3.pointer(event, svgRef.current);
        showSelectionByIndex(nearestIndexByPx(px));
      });

    const svgEl = svgRef.current;

    d3.select(svgEl).on("pointerleave", () => {
      // If the chart still has keyboard focus when the mouse leaves, fall
      // back to keyboard-navigation mode so the user can keep arrow-keying.
      if (document.activeElement === svgEl) {
        keyboardModeRef.current = true;
        showSelectionByIndex(keyboardIndexRef.current);
      } else {
        hideSelection();
      }
    });

    const handleKeyDown = e => {
      // Keyboard zoom: +/- (and =) zoom the focused chart in / out, and 0
      // resets it. Available whenever zooming is enabled and the chart has
      // focus, independent of crosshair (arrow-key) navigation mode.
      if (enableZoom && zoomBehavior) {
        if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          d3.select(svgEl).transition().duration(150).call(zoomBehavior.scaleBy, 1.3);
          return;
        }
        if (e.key === "-" || e.key === "_") {
          e.preventDefault();
          d3.select(svgEl).transition().duration(150).call(zoomBehavior.scaleBy, 1 / 1.3);
          return;
        }
        if (e.key === "0") {
          e.preventDefault();
          d3.select(svgEl).transition().duration(150).call(zoomBehavior.transform, d3.zoomIdentity);
          return;
        }
      }
      if (!keyboardModeRef.current || !anchors.length) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (e.shiftKey) {
          const targetTime = anchors[keyboardIndexRef.current].getTime() - MS_PER_HOUR;
          showSelectionByIndex(nearestIndexByTime(targetTime));
        } else if (keyboardIndexRef.current > 0) {
          showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (e.shiftKey) {
          const targetTime = anchors[keyboardIndexRef.current].getTime() + MS_PER_HOUR;
          showSelectionByIndex(nearestIndexByTime(targetTime));
        } else if (keyboardIndexRef.current < anchors.length - 1) {
          showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      if (!anchors.length) return;
      keyboardModeRef.current = true;
      showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      hideSelection();
    };

    svgEl.addEventListener("keydown", handleKeyDown);
    svgEl.addEventListener("focus", handleFocus);
    svgEl.addEventListener("blur", handleBlur);

    // If the chart was already focused when this effect re-ran (e.g. data
    // updated while the user was navigating with the keyboard), re-enter
    // keyboard mode and re-render the selection so the crosshair persists.
    if (document.activeElement === svgEl) {
      handleFocus();
    }

    // Scroll / drag / keyboard zoom (only when enabled). Re-scales the x axis
    // and re-positions the lines, circles and any visible crosshair against
    // the zoomed scale. The y axis is left untouched.
    if (enableZoom) {
      const redrawX = () => {
        xAxisGroup.call(xAxis.scale(zx));
        styleXAxis();
        seriesPaths.forEach(path => path.attr("d", makeLine(zx)));
        seriesCircles.forEach(circles => {
          if (circles) circles.attr("cx", d => zx(d.date));
        });
        if (+crosshair.style("opacity") === 1) {
          updateCrosshair(keyboardIndexRef.current);
          if (onTooltipRef.current) showTooltipForPoint(keyboardIndexRef.current);
        }
      };

      zoomBehavior = d3
        .zoom()
        .scaleExtent([1, 30])
        .translateExtent([
          [plotLeft, plotTop],
          [plotRight, plotBottom],
        ])
        .extent([
          [plotLeft, plotTop],
          [plotRight, plotBottom],
        ])
        // Block the default double-click-to-zoom so it doesn't fight the
        // crosshair, but keep wheel and drag-to-pan.
        .filter(event => event.type !== "dblclick" && !event.button)
        .on("zoom", event => {
          zx = event.transform.rescaleX(xScale);
          redrawX();
        });

      d3.select(svgEl).call(zoomBehavior).on("dblclick.zoom", null);
      // Reset any transform persisted on the node from a previous draw so the
      // chart starts un-zoomed whenever it's rebuilt (e.g. on a range change).
      d3.select(svgEl).call(zoomBehavior.transform, d3.zoomIdentity);
    }

    overlay.raise();

    return () => {
      svgEl.removeEventListener("keydown", handleKeyDown);
      svgEl.removeEventListener("focus", handleFocus);
      svgEl.removeEventListener("blur", handleBlur);
      if (enableZoom) {
        d3.select(svgEl).on(".zoom", null);
      }
    };
  }, [
    seriesList,
    hasData,
    isMulti,
    dimensions,
    isCustomRange,
    domain,
    tickValues,
    unit,
    tooltipUnit,
    midlines,
    safeId,
    clipPlotId,
    monthlyTicks,
    startDate,
    endDate,
    renderTooltipExtra,
    showPoints,
    enableZoom,
    scaleYToDataExtent,
    fixedYDomain,
    dateFormatter,
    tickFormat,
    yGrid,
    dailyTicks,
    showHoverDots,
  ]);

  return (
    <div
      ref={wrapperRef}
      className={wrapperClassName}
      style={{ width: "100%", height: "auto", minWidth: 0 }}
      data-duration={duration || "custom"}
    >
      {(headerLeft || headerRight) && (
        <div className="header">
          <span>{headerLeft}</span>
          <span>{headerRight}</span>
        </div>
      )}
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Time-series chart. Use left and right arrow keys to navigate between data points. Hold Shift to jump one hour."
        style={{ width: "100%", height: `${height}px`, display: "block" }}
      />
      {showLegend ? (
        <div className="chart-legend">
          {seriesList
            .filter(s => s.label)
            .map(s => (
              <span key={s.label} style={{ color: s.color }}>
                <svg width={28} height={10} aria-hidden="true">
                  <line
                    x1={2}
                    y1={5}
                    x2={26}
                    y2={5}
                    stroke={s.color}
                    strokeWidth={2}
                    strokeDasharray={s.dash || undefined}
                  />
                </svg>
                {s.label}
              </span>
            ))}
        </div>
      ) : null}
    </div>
  );
};

export default MultiLineChart;
